import {
  Breadcrumb,
  Button,
  Card,
  Empty,
  Form,
  Input,
  Modal,
  Select,
  Tabs,
} from "antd";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import AccountService from "../../../../services/accounts.service";
import moment from "moment";
import { useSelector } from "react-redux";
import { accountListSelector, modeSelector } from "../../../../store/selectors/app.selector";
import {
  allowDecimalwithMaxLimit,
  allowOnlyAlphaNumericsShouldNotStartWithSpace,
  amountFormat,
} from "../../../../services/common.service";
import { ReactComponent as TitleIcon } from "../../../../assests/images/accountBread.svg";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import { ReactComponent as TickIcon } from "../../../../assests/images/tickIcon.svg";
import OTP from "../../../../components/otp";
import { ReactComponent as HomeIcon } from "../../../../assests/images/home-line.svg";

export default function Repayment() {
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [activeKey, setActiveKey] = useState("ACCOUNT_DETAILS");
  const [accountDetails, setAccountDetails] = useState({});
  const accList = useSelector(accountListSelector);
  const [details, setDetails] = useState({});
  const [stage, setStage] = useState(1);
  const [formDetails, setFormDetails] = useState({});
  const [otp, setOtp] = useState("");
  const [successVisible, setSuccessVisible] = useState(false);
  const [completedDetails, setCompletedDetails] = useState({});
  const [accounts, setAccount] = useState(accList);
  const mode = useSelector(modeSelector);

  useEffect(() => {
    if (location.pathname === "/accounts/loan/repayment") {
      setActiveKey("REPAYMENT");
    }
    if (location.state) {
      setAccountDetails(location.state);
    }
  }, []);

  useEffect(() => {
    const list = accList.filter(
      (account) => account.accountCurrency === accountDetails.accountCcy
    );
    setAccount(list);
  }, [accountDetails]);

  const handleTabClick = (key) => {
    setActiveKey(key);
    if (key === "ACCOUNT_DETAILS") {
      navigate("/accounts/loan/summary", { state: accountDetails });
    }
    if (key === "DISBURSEMENT_INQUIRY") {
      navigate("/accounts/loan/disbursement-inquiry", {
        state: accountDetails,
      });
    }
    if (key === "SCHEDULE_INQUIRY") {
      navigate("/accounts/loan/schedule-inquiry", {
        state: accountDetails,
      });
    }
    if (key === "DETAILED_STATEMENT") {
      navigate("/accounts/loan/statement", {
        state: accountDetails,
      });
    }
  };

  const handleAccountNo = (value) => {
    const accountDet = accounts.find((ele) => ele.accountNo === value);
    setDetails(accountDet);
  };

  const onVerify = useCallback(
    async (values) => {
      try {
        const transferAmount =
          typeof values.amount === "number"
            ? values.amount
            : parseFloat(values.amount.replaceAll(/,/g, ""));
        const reqParams = {
          accountNo: values.accountNo,
          brnCode: details.branchId,
          currency: details.accountCurrency,
          amount: transferAmount,
          loanAccountNo: accountDetails.accountNo,
          loanAccBrn: accountDetails.branchId,
          remarks: values.remarks,
        };
        setFormDetails(reqParams);
        const response = await AccountService.repaymentVerify(reqParams);
        if (response.status === 200) {
          if (response.data["otpEnable"]) {
            setStage(2);
          }
        }
      } catch (error) {}
    },
    [accountDetails, details]
  );
  const onConfirm = useCallback(
    async (values) => {
      try {
        const response = await AccountService.repaymentConfirm(values);
        if (response.status === 200) {
          setSuccessVisible(true);
          setCompletedDetails(response.data);
        }
      } catch (error) {
        setOtp("");
      }
    },
    [accountDetails, details]
  );

  const handleResendOtp = useCallback(async (value) => {
    try {
      const response = await AccountService.repaymentVerify(value);
      if (response.status === 200) {
      }
    } catch (error) {
      setOtp("");
    }
  }, []);

  function renderSwitch() {
    switch (stage) {
      case 1:
        return renderForm();

      case 2:
        return (
          <OTP
            otp={otp}
            setOtp={setOtp}
            onConfirm={(values) => onConfirm({ ...formDetails, ...values })}
            handleResendOtp={() => handleResendOtp({ ...formDetails })}
            cancelTransaction={() => {
              navigate("/accounts");
            }}
          />
        );

      default:
        return renderForm();
    }
  }

  function renderForm() {
    return (
      <>
        {" "}
        <Form layout="vertical" form={form} onFinish={onVerify}>
          <div className="account-details my-3">
            <label>
              {t("pages.my_accounts.loans.loan_acc_no", {
                defaultValue: "Investment Account Number",
              })}
            </label>
            <span>{accountDetails?.accountNo || "-"}</span>
          </div>
          <Form.Item
            label={t("pages.gepeg.source_account", {
              defaultValue: "Source Account",
            })}
            name="accountNo"
            rules={[
              {
                required: true,
                message: t("pages.my_accounts.deposits.source_account_no_req", {
                  defaultValue: "Source Account is required",
                }),
              },
            ]}
          >
            <Select
              placeholder={t("pages.gepeg.source_account", {
                defaultValue: "Source Account",
              })}
              optionLabelProp={"value"}
              size="large"
              onChange={(value) => handleAccountNo(value)}
            >
              {accounts.map((option, idx) => (
                <Select.Option key={idx} value={option.accountNo}>
                  <span className="acc-short-name">
                    {option?.acctShortName}
                  </span>
                  <br />
                  <span style={{ fontSize: "12px" }}>{option?.accountNo}</span>
                  <br />
                  <span style={{ fontSize: "12px" }}>{option?.branchName}</span>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label={t("common.amount", {
              defaultValue: "Amount",
            })}
            name={"amount"}
            rules={[
              {
                required: true,
                message: t("errorMesssages.amount_required", {
                  defaultValue: "Amount is required",
                }),
              },
              {
                validator: (_, value) => {
                  if (parseFloat(value) <= 0) {
                    return Promise.reject(
                      t("errorMesssages.amount_valid", {
                        defaultValue: "Amount should be greater than 0",
                      })
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <NumericFormat
              size="large"
              type="tel"
              placeholder={t("common.amount", {
                defaultValue: "Amount",
              })}
              maxLength={12}
              allowNegative={false}
              customInput={Input}
              thousandSeparator={true}
              fixedDecimalScale={false}
              onKeyPress={(e) => allowDecimalwithMaxLimit(e)}
            />
          </Form.Item>
          <Form.Item
            label={t("common.remarks", { defaultValue: "Reason" })}
            name="remarks"
            rules={[
              {
                required: true,
                message: t("errorMesssages.remark_require", {
                  defaultValue: "Reason is required",
                }),
              },
            ]}
          >
            <Input
              size="large"
              maxLength={30}
              placeholder={t("common.remarks", { defaultValue: "Reason" })}
              onKeyPress={(e) => allowOnlyAlphaNumericsShouldNotStartWithSpace(e)}
            />
          </Form.Item>
          <div align="right">
            {" "}
            <Button
              type="primary"
              className="primary-btn"
              size="large"
              htmlType="submit"
            >
              {t("buttons.submit_btn", {
                defaultValue: "Submit",
              })}
            </Button>
          </div>
        </Form>
      </>
    );
  }

  return (
    <>
        <Breadcrumb separator=">">
          {/* <Breadcrumb.Item>
            <span onClick={() => navigate("/dashboard")}>
              <HomeIcon height={14}/>
            </span>
          </Breadcrumb.Item> */}
          <Breadcrumb.Item>
            <span onClick={() => navigate("/accounts")}>
              {t("navigation.account", {
                defaultValue: "Accounts",
              })}
            </span>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <span>
              {t("pages.my_accounts.loans.repayment", {
                defaultValue: "Repayment",
              })}
            </span>
          </Breadcrumb.Item>
        </Breadcrumb>

      <Card size="small" className="mb-1 text-center breadcrumb-title-card">
        <div className="row">
          <div className="col-xs-12 col-sm-12 col-md-12 col-lg-12 col-xl-12 d-flex justify-content-left align-items-center heading mx-0">
            {/* <TitleIcon height={30} style={{ marginLeft: "-20px" }} /> */}
            <h3 className="title mb-0">
              {t("pages.my_accounts.loans.repayment", {
                defaultValue: "Repayment",
              })}
            </h3>
          </div>
        </div>
      </Card>

      <Card className="account-tab-card mb-1">
        <Tabs
          defaultActiveKey="1"
          type="line"
          onChange={setActiveKey}
          activeKey={activeKey}
          onTabClick={handleTabClick}
        >
          <Tabs.TabPane
            tab={t("pages.my_accounts.loans.loan_and_finance_details", {
              defaultValue: "Investment and Finance Details",
            })}
            key="ACCOUNT_DETAILS"
          ></Tabs.TabPane>
          <Tabs.TabPane
            tab={t("buttons.view_stmt_btn", {
              defaultValue: "View Statement",
            })}
            key="DETAILED_STATEMENT"
          ></Tabs.TabPane>
          {["I", "AA"].indexOf(mode) > -1 && (
            <Tabs.TabPane
              tab={t("pages.my_accounts.loans.repayment", {
                defaultValue: "Repayment",
              })}
              key="REPAYMENT"
            ></Tabs.TabPane>
          )}
          <Tabs.TabPane
            tab={t("pages.my_accounts.loans.disbursement_inquiry", {
              defaultValue: "Disbursement Inquiry",
            })}
            key="DISBURSEMENT_INQUIRY"
          ></Tabs.TabPane>
          <Tabs.TabPane
            tab={t("pages.my_accounts.loans.schedule_inquiry", {
              defaultValue: "Schedule Inquiry",
            })}
            key="SCHEDULE_INQUIRY"
          ></Tabs.TabPane>
        </Tabs>
      </Card>
      <Card className="application-card col-xs-12 col-sm-12 col-md-6 col-lg-6 col-xl-6 mt-2">{renderSwitch()}</Card>
      <Modal
        title={""}
        open={successVisible}
        footer={null}
        width={480}
        closable={false}
        maskClosable={false}
        mask={true}
      >
        <div style={{   margin: "0 auto" }} align="center">
          <TickIcon height={65} />
          <h4 className="mb-3 mt-2">
            {t("common.success", {
              defaultValue: "Success",
            })}
          </h4>
          <p>{completedDetails.message}</p>

          <div align="center">
            <Button
              size="large"
              style={{ width: 130 }}
              onClick={() => {
                setSuccessVisible(false);
                navigate("/accounts");
              }}
              type="primary"
            >
              {t("buttons.okay_btn", {
                defaultValue: "Okay",
              })}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
